import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, DateRangePicker, TagInput, IconButton } from "rsuite";
import CloseIcon from "@rsuite/icons/Close";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import RenderTags from "../renderTags/RenderTags";
import { formatDateRange } from "../../../utils/dateUtils";

import "./multiDateRangePicker.scss";

const { beforeToday } = DateRangePicker;

const MS_PER_DAY = 86400000;
const atMidnight = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Dates the calendar refuses.
 *
 * Past dates always. Then, once the first date of a range is picked, the window is
 * `maxNights` nights on EITHER side of it — so anchoring on 30 Oct opens 2 Oct through
 * 27 Nov — with `beforeToday` still clipping anything earlier than today.
 *
 * Both directions on purpose: the first click is just one END of the range, not
 * necessarily the arrival. Someone who knows their departure date picks it first and
 * works backwards. rsuite normalises the pair to [earlier, later], so a backward pick
 * cannot produce an inverted range — the only thing the cap has to police is the span.
 *
 * ⚠ Deliberately does NOT bail on `selectedDone`, which is where rsuite's own helpers
 * (and `allowedMaxDays`) stop. Once both ends are picked they treat the range as
 * finished and re-enable the whole calendar, so a completed 18 Sep–16 Oct range sits
 * there with every later date looking selectable. Nothing invalid can actually be
 * submitted that way — clicking a far date starts a fresh range, which is capped again
 * — but the calendar is lying about what is allowed. Keeping the window enforced while
 * the picker is open is the honest thing to show.
 *
 * Trade-off, accepted: to start a range OUTSIDE the current window the picker has to be
 * cleared or reopened, because those dates stay disabled.
 *
 * Still gated on `target === "CALENDAR"` so only day cells are constrained — the OK
 * control and other targets are left alone.
 */
const buildDisabledDate = (maxNights) => {
  const isPastDate = beforeToday();

  return (date, selectValue, selectedDone, target) => {
    if (isPastDate(date, selectValue, selectedDone, target)) return true;
    if (!(maxNights > 0)) return false;

    const anchor = selectValue?.[0];
    if (!anchor || target !== "CALENDAR") return false;

    const nights = Math.round(
      (atMidnight(date) - atMidnight(anchor)) / MS_PER_DAY
    );

    // Further than the stay-length cap from the anchor, in either direction.
    return Math.abs(nights) > maxNights;
  };
};

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const renderValue = RenderTags();

// Helper function to check if date ranges are the same
export const areDateRangesEqual = (range1, range2) => {
  if (!range1 || !range2 || !Array.isArray(range1) || !Array.isArray(range2)) {
    return false;
  }

  const [start1, end1] = range1;
  const [start2, end2] = range2;

  // Convert to dayjs objects for comparison
  const start1Date = dayjs(start1);
  const end1Date = dayjs(end1);
  const start2Date = dayjs(start2);
  const end2Date = dayjs(end2);

  // Check if both start and end dates are the same
  return (
    start1Date.isSame(start2Date, "day") && end1Date.isSame(end2Date, "day")
  );
};

const MultiDateRangePicker = ({
  dateRanges,
  setDateRanges,
  className = "",
  maxNights,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isAddMoreDatesOpen, setIsAddMoreDatesOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsPickerOpen(false);
        setIsAddMoreDatesOpen(false);
      }
    };

    const isClickOutside = (event) => {
      if (
        !event.target.closest(".rs-picker-popup") &&
        !event.target.closest(".rs-picker-textbox") &&
        !event.target.closest(".rs-calendar-table-cell-content") &&
        !event.target.closest(".rs-calendar-month-dropdown-cell-content") &&
        !event.target.closest(".add-more-dates") &&
        !event.target.closest(".dateRangePicker-container") &&
        !event.target.closest(".date-range-remove-btn")
      ) {
        setIsPickerOpen(false);
        setIsAddMoreDatesOpen(false);
      }
    };

    const addListener = () => {
      document.addEventListener("click", isClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    };

    const removeListener = () => {
      document.removeEventListener("click", isClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };

    if (isPickerOpen || isAddMoreDatesOpen) {
      addListener();
    } else {
      removeListener();
    }

    return () => {
      removeListener();
    };
  }, [isPickerOpen, isAddMoreDatesOpen]);

  const handleOnOk = (value) => {
    // Check if the same date range already exists
    const isDuplicate = (dateRanges || []).some((existingRange) =>
      areDateRangesEqual(existingRange, value)
    );

    if (isDuplicate) {
      toast.error("This date range is already selected");
      return;
    }
    setDateRanges([...(dateRanges || []), value]);
    setIsPickerOpen(false);
    setIsAddMoreDatesOpen(false);
  };

  const handleOnClean = () => {
    setDateRanges([]);
  };

  const handleOnTagRemove = (event, index) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
    const newDateRange = [...(dateRanges || [])];
    newDateRange.splice(index, 1);
    setDateRanges(newDateRange);

    // If this was the last date range, reset the add-more state
    if (newDateRange.length === 0) {
      setIsAddMoreDatesOpen(false);
    }
  };

  const handleOnFocus = () => {
    setIsPickerOpen(true);
    // Reset add more dates state when focusing on TagInput
    setIsAddMoreDatesOpen(false);
  };

  return (
    <div
      className={`multi-date-range-picker ${className}`}
      data-testid="multi-date-range-picker"
    >
      <TagInput
        data-testid="multi-date-tag-input"
        block
        searchable={false}
        placeholder={Placeholder}
        onClean={handleOnClean}
        onTagRemove={handleOnTagRemove}
        value={(dateRanges || []).map(formatDateRange)}
        renderValue={renderValue}
        onFocusCapture={handleOnFocus}
        onEnter={handleOnFocus}
      />
      {isPickerOpen && (
        <div
          className="dateRangePicker-wrapper"
          data-testid="date-picker-wrapper"
        >
          {/* Show add-more-dates section when dates exist */}
          {(dateRanges || []).length > 0 && (
            <div
              className="dateRangePicker-container"
              data-testid="date-picker-container"
            >
              {/* Show "Add more dates" button only when not in add-more mode */}
              {!isAddMoreDatesOpen && (
                <Button
                  minimal
                  className="add-more-dates"
                  data-testid="add-more-dates-button"
                  onClick={() => {
                    setIsAddMoreDatesOpen(true);
                  }}
                >
                  Add more dates
                </Button>
              )}

              {/* Show DateRangePicker when in add-more mode */}
              {isAddMoreDatesOpen && (
                <DateRangePicker
                  className="dateRangePicker"
                  data-testid="date-range-picker"
                  showHeader={false}
                  open={isPickerOpen}
                  onOk={handleOnOk}
                  value={[]}
                  ranges={[]}
                  // Past dates, plus anything beyond maxNights from the end the user
                  // picked first. Overlapping ranges stay allowed.
                  shouldDisableDate={buildDisabledDate(maxNights)}
                />
              )}

              {(dateRanges || [])?.map((v, index) => (
                <div key={v?.toString()} className="date-range-item">
                  <span className="date-range-text">{formatDateRange(v)}</span>
                  <IconButton
                    icon={<CloseIcon />}
                    size="sm"
                    appearance="subtle"
                    onClick={() => handleOnTagRemove(null, index)}
                    aria-label="Remove date range"
                    data-testid="remove-date"
                    className="date-range-remove-btn"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Show DateRangePicker when: 
              1. No dates selected (dateRanges.length === 0), OR
              2. User clicked "Add more dates" (isAddMoreDatesOpen === true) */}
          {(dateRanges || []).length === 0 && (
            <DateRangePicker
              className="dateRangePicker"
              data-testid="date-range-picker"
              showHeader={false}
              open={isPickerOpen}
              onOk={handleOnOk}
              value={[]}
              ranges={[]}
              // Past dates, plus anything beyond maxNights from the end the user
              // picked first. Overlapping ranges stay allowed.
              //
              // ⚠ This is the branch shown FIRST (no ranges yet) — the other picker
              // above only appears once "Add more dates" is used. Both must carry the
              // same rule or the cap silently does nothing on the common path.
              shouldDisableDate={buildDisabledDate(maxNights)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const Placeholder = (
  <>
    Choose arrival date <strong> → </strong> Choose departure date
  </>
);

MultiDateRangePicker.propTypes = {
  dateRanges: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
  ).isRequired,
  setDateRanges: PropTypes.func.isRequired,
  className: PropTypes.string,
  /**
   * Longest selectable stay, in nights. Omit for no cap — the limit belongs to the
   * caller's API, not to this shared picker, so the default leaves behaviour unchanged.
   */
  maxNights: PropTypes.number,
};

export default MultiDateRangePicker;
